import React, {useEffect, useRef, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from "@material-ui/core";
import 'firebase/database';
import firebase from 'firebase/app';
import OT from '@opentok/client';
import MuiAlert from '@material-ui/lab/Alert';

import './Video.css';
import {DB_CONFIG} from '../../../../utils/Constants';
import ExamStats from "./ExamStats";


const Alert = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const Video = ({studentDetails, removeStudentFromExamHall}) => {
  const publisherRef = useRef(null);
  const sessionRef = useRef(null);
  const subscriberRef = useRef(null);
  const [mute, setMute] = useState(true);
  const [subscriberConnected, setSubscriberConnected] = useState(false);
  const [subscriberLoading, setSubscriberLoading] = useState(false);
  const [subscriberError, setSubscriberError] = useState(null);

  const [examStats, setExamStats] = useState({
    appToggleCount: 0,
    internetFailureCount: 0,
    time: null,
    videoCallFailureCount: 0,
    examSubmit: false
  });
  const [dialogContent, setDialogContent] = useState({
    title: null,
    message: null,
    action: null,
  });

  const app = useRef();
  const database = useRef();
  const firstTime = useRef(true);

  const endCall = () => {
  };

  const pauseExam = () => {
    const examAttemptId = studentDetails.examAttemptId;
    const studentId = studentDetails.student.id;
    app.current.database().ref().child(`VideoStatus/${examAttemptId}/${studentId}`).set(false);
  };

  const endExam = () => {
    const examAttemptId = studentDetails.examAttemptId;
    const studentId = studentDetails.student.id;
    app.current.database().ref().child(`ExamStatus/${examAttemptId}/${studentId}`).set(false);
  };

  const toggleMute = () => {
    const newMuteState = !mute;
    if (publisherRef.current) {
      publisherRef.current.publishAudio(!newMuteState);
    }
    setMute(newMuteState);
  }

  const handleCloseSnackbar = () => {
  };

  useEffect(() => {
    const handleStatsChange = snap => {
      const studentId = studentDetails.student.id;

      if (snap.val()) {
        const stats = snap.val();
        if (!firstTime.current && stats.examSubmit) {
          setDialogContent({
            title: 'Exam Submit',
            message: studentDetails.student.fullName + " has submitted the exam.",
            action: () => {
              endCall();
              removeStudentFromExamHall(studentId);
            }
          })
        } else if (firstTime.current && stats.examSubmit) {
          endCall();
          removeStudentFromExamHall(studentId);
        }

        firstTime.current = false;
        setExamStats(stats);
      }
    };

    const examAttemptId = studentDetails.examAttemptId;
    const studentId = studentDetails.student.id;

    if (!firebase.apps.length) {
      app.current = firebase.initializeApp(DB_CONFIG);
      database.current = app.current.database().ref().child(`ExamStats/${examAttemptId}/${studentId}`);
    } else {
      app.current = firebase;
      database.current = app.current.database().ref().child(`ExamStats/${examAttemptId}/${studentId}`);
    }

    database.current.on('value', handleStatsChange);

    // Initialize OpenTok session
    const session = OT.initSession(process.env.REACT_APP_TOKBOX_API_KEY, studentDetails.sessionId);
    sessionRef.current = session;

    // Initialize publisher
    const publisher = OT.initPublisher('publisher-container', {
      audioFallback: {
        publisher: true,
        subscriber: false,
      },
      fitMode: 'contain',
      publishVideo: false,
      publishAudio: !mute,
      insertMode: 'append',
      width: '100%',
      height: '100%',
    }, (error) => {
      if (error) {
        console.error('Error initializing publisher:', error);
      } else {
        console.log('Publisher initialized successfully');
      }
    });

    publisherRef.current = publisher;

    // Connect to session and publish
    session.connect(studentDetails.token, (error) => {
      if (error) {
        console.error('Error connecting to session:', error);
      } else {
        console.log('Connected to session');
        session.publish(publisher, (publishError) => {
          if (publishError) {
            console.error('Error publishing:', publishError);
          } else {
            console.log('Publishing successfully');
          }
        });
      }
    });

    // Handle incoming streams (student video)
    session.on('streamCreated', (event) => {
      setSubscriberLoading(true);
      setSubscriberError(null);

      const options = {
        height: "100%",
        width: "100%",
        insertMode: 'append',
        fitMode: 'contain',
        subscribeToAudio: true,
        subscribeToVideo: true,
      };

      const subscriber = session.subscribe(event.stream, studentDetails.student.id, options, (error) => {
        if (error) {
          console.error('Error subscribing to stream:', error);
          setSubscriberLoading(false);
          setSubscriberError(error.message || 'Failed to load video stream');
          setSubscriberConnected(false);
        } else {
          console.log('Subscribed to student stream');
          subscriberRef.current = subscriber;

          // Handle subscriber events
          subscriber.on('videoElementCreated', () => {
            setSubscriberLoading(false);
            setSubscriberConnected(true);
            setSubscriberError(null);
          });

          subscriber.on('videoDisabled', () => {
            console.log('Student video disabled');
          });

          subscriber.on('videoEnabled', () => {
            console.log('Student video enabled');
          });
        }
      });
    });

    session.on('streamDestroyed', (event) => {
      console.log('Stream destroyed');
      subscriberRef.current = null;
      setSubscriberConnected(false);
      setSubscriberLoading(false);
      setSubscriberError(null);
    });

    // Cleanup function
    return () => {
      database.current.off('value', handleStatsChange);

      if (publisherRef.current) {
        publisherRef.current.destroy();
        publisherRef.current = null;
      }

      if (sessionRef.current) {
        sessionRef.current.disconnect();
        sessionRef.current = null;
      }

      endCall();
    }
  }, []);

  return (
    <div className="mainContainerDiv">
      {examStats.examSubmit ? (
        <ExamStats
          fullName={studentDetails.student.fullName}
          examStats={examStats}
          pauseExam={null}
          endExam={null}
          showPauseButton={false}
          examSubmit={true}/>
      ) : (
        <>
          <div className={'video-container'}>
            <div id="publisher-container" style={{width: '100%', height: '100%'}}/>
            <div id={studentDetails.student.id} style={{width: '100%', height: '100%', position: 'relative'}}>
              {subscriberLoading && !subscriberConnected && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#666',
                  zIndex: 10
                }}>
                  <div>Connecting to student video...</div>
                </div>
              )}
              {subscriberError && !subscriberConnected && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#f44336',
                  padding: '20px',
                  zIndex: 10
                }}>
                  <div style={{fontWeight: 'bold', marginBottom: '8px'}}>Connection Error</div>
                  <div style={{fontSize: '14px'}}>{subscriberError}</div>
                </div>
              )}
            </div>
          </div>
          <ExamStats
            fullName={studentDetails.student.fullName}
            examStats={examStats}
            pauseExam={pauseExam}
            endExam={endExam}
            showPauseButton={subscriberConnected}
            mute={mute}
            toggleMute={toggleMute}/>
        </>
      )}

      {dialogContent.title && (
        <Dialog
          open
          aria-labelledby="video-dialog-title"
        >
          <DialogTitle id="video-dialog-title" className={"dialog-title"}>
            {dialogContent.title}
          </DialogTitle>
          <DialogContent className="dialog-content">
            {dialogContent.message}
          </DialogContent>
          <DialogActions className="actions-container">
            <Button size="small" key="ok" variant="contained" color="primary" onClick={() => {
              dialogContent.action();
              setDialogContent({
                title: null,
                message: null,
                action: null,
              });
            }}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/*{backgroundWarning && (*/}
      {/*  <Box mt={2}>*/}
      {/*    <Alert severity={'warning'} onClose={handleCloseSnackbar}>*/}
      {/*      Caution: The student has either rotated the phone or attempted to switch the app.*/}
      {/*    </Alert>*/}
      {/*  </Box>*/}
      {/*)}*/}
    </div>
  );
};

export default Video;
